import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Analytics as AnalyticsIcon,
  PeopleOutline as PeopleIcon,
  Devices as DevicesIcon,
  AccessTime as AccessTimeIcon,
  Circle as CircleIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  PersonOutline as PersonOutlineIcon,
  CorporateFare as CorporateFareIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../../AuthContext';
import { logAnalyticsEvent } from '../../firebase';
import configData from '../../configure';
import { getStoredLiveSessions, subscribeToPresenceUpdates } from '../../utils/presenceService';
import './FirebaseAnalytics.css';

// Helper to determine device type from User-Agent or client string
const parseDeviceInfo = (userAgentStr = '') => {
  const ua = userAgentStr || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

  if (isMobile) {
    if (/iPhone/i.test(ua)) return { label: 'Mobile (iOS • iPhone)', type: 'mobile' };
    if (/iPad/i.test(ua)) return { label: 'Tablet (iPadOS)', type: 'mobile' };
    if (/Android/i.test(ua)) return { label: 'Mobile (Android • EducateMobile)', type: 'mobile' };
    return { label: 'Mobile App (EducateMobile)', type: 'mobile' };
  }

  if (/Macintosh|Mac OS X/i.test(ua)) {
    if (/Chrome/i.test(ua)) return { label: 'Web (Chrome • macOS)', type: 'desktop' };
    if (/Safari/i.test(ua)) return { label: 'Web (Safari • macOS)', type: 'desktop' };
    if (/Firefox/i.test(ua)) return { label: 'Web (Firefox • macOS)', type: 'desktop' };
    return { label: 'Web (macOS)', type: 'desktop' };
  }

  if (/Windows/i.test(ua)) {
    if (/Edg/i.test(ua)) return { label: 'Web (Edge • Windows)', type: 'desktop' };
    if (/Chrome/i.test(ua)) return { label: 'Web (Chrome • Windows)', type: 'desktop' };
    if (/Firefox/i.test(ua)) return { label: 'Web (Firefox • Windows)', type: 'desktop' };
    return { label: 'Web (Windows PC)', type: 'desktop' };
  }

  return { label: 'Web (Desktop Browser)', type: 'desktop' };
};

// Format timestamps into relative/friendly times
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'Recently';
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return String(timestamp);

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 2) return 'Just now';
    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffHours < 24) return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    if (diffDays === 1) return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (err) {
    return 'Recently';
  }
};

const FirebaseAnalytics = ({ orgId: propOrgId, embedded = false }) => {
  const { user: currentUser, userRole, orgId: authOrgId, parentOrgId: authParentOrgId, axios, token } = useAuth();

  // Organization state strictly scoped to this organization (never parent organization)
  const [currentOrgName, setCurrentOrgName] = useState('');

  // Resolve reliable organization ID strictly for this organization (never parent org)
  const effectiveOrgId = useMemo(() => {
    let org = propOrgId || authOrgId || currentUser?.orgId || currentUser?.orgDetails?.orgId || '';
    if (!org && typeof window !== 'undefined') {
      org = localStorage.getItem('activeOrgId') || localStorage.getItem('orgId') || '';
      if (!org) {
        const upiKey = Object.keys(localStorage).find(k => k.startsWith('org_upi_'));
        if (upiKey) org = upiKey.replace('org_upi_', '');
      }
    }
    return org || '';
  }, [propOrgId, authOrgId, currentUser]);

  // Fetch organization metadata strictly for that organization (never include parent org)
  useEffect(() => {
    let isMounted = true;
    const fetchOrgMetadata = async () => {
      const activeId = effectiveOrgId;
      if (!activeId || !axios) return;

      try {
        const orgRes = await axios.get(
          configData.get_identity_identity_org_orgid
            ? configData.get_identity_identity_org_orgid(activeId, activeId)
            : `${configData.IDENTITY_ORG}/${activeId}`,
          { headers: { accept: 'application/hal+json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
        );

        if (!isMounted) return;
        const orgData = orgRes.data || {};
        const name = orgData.orgName || orgData.name || currentUser?.orgName || '';
        if (name) setCurrentOrgName(name);
      } catch (err) {
        console.warn('[FirebaseAnalytics] Organization details fetch notice:', err);
        if (isMounted) {
          setCurrentOrgName(currentUser?.orgName || 'Active Organization');
        }
      }
    };

    fetchOrgMetadata();
    return () => { isMounted = false; };
  }, [effectiveOrgId, axios, token, currentUser]);


  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ACTIVE_NOW');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userList, setUserList] = useState([]);
  const [lastUpdated, setLastUpdated] = useState('');
  const [fetchError, setFetchError] = useState(null);

  // SEO Page Title and Firebase Analytics tagging with org_id
  useEffect(() => {
    if (!embedded) {
      document.title = "Firebase Analytics | eGurukul";
    }
    logAnalyticsEvent('view_analytics_dashboard', {
      user_role: userRole || 'GUEST',
      screen_name: 'FirebaseAnalytics',
      org_id: effectiveOrgId || 'default'
    });
  }, [embedded, userRole, effectiveOrgId]);

  // Fetch real users from backend APIs strictly isolated for the active organization
  const fetchAllUsers = useCallback(async () => {
    setIsRefreshing(true);
    setFetchError(null);

    try {
      const targetOrg = effectiveOrgId ? String(effectiveOrgId).trim() : '';
      if (!targetOrg) {
        setUserList([]);
        setIsRefreshing(false);
        return;
      }

      const knownParentOrgId = (authParentOrgId || currentUser?.parentOrgId || currentUser?.orgDetails?.parentOrgId || '').trim();

      const liveSessions = getStoredLiveSessions(targetOrg);
      const rawUserPool = [];
      const seenEmails = new Set();
      const seenIds = new Set();

      // 1. Include currently authenticated user ONLY if they strictly belong to this active organization (never parent org)
      if (currentUser) {
        const currentUserOrg = currentUser.orgId || currentUser.orgDetails?.orgId || '';
        const isCurrentUserInOrg = currentUserOrg && String(currentUserOrg).trim().toLowerCase() === targetOrg.toLowerCase();
        const isParentOrgUser = knownParentOrgId && String(currentUserOrg).trim().toLowerCase() === knownParentOrgId.toLowerCase() && targetOrg.toLowerCase() !== knownParentOrgId.toLowerCase();

        if (isCurrentUserInOrg && !isParentOrgUser) {
          const currentEmail = (currentUser.email || '').toLowerCase().trim();
          const currentName = currentUser.name ||
            `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
            currentUser.username ||
            currentEmail.split('@')[0] ||
            'Current User';

          const currentDev = parseDeviceInfo(navigator.userAgent);

          const me = {
            id: currentUser.userId || currentUser.id || 'usr-current',
            name: `${currentName} (You)`,
            email: currentEmail || 'current.user@egurukulapps.com',
            role: (userRole || currentUser.role || 'ADMIN').toUpperCase(),
            status: 'ACTIVE_NOW',
            currentScreen: 'Firebase Analytics Dashboard',
            device: currentDev.label,
            deviceType: currentDev.type,
            monthlySessions: 12,
            lastSeen: 'Active right now',
            lastSeenDate: new Date(),
            branch: currentOrgName || currentUser.orgName || 'Main Campus',
            isCurrentUser: true,
            userStatus: 'ACTIVE',
            orgId: targetOrg
          };

          rawUserPool.push(me);
          if (currentEmail) seenEmails.add(currentEmail);
          if (me.id) seenIds.add(me.id);
        }
      }

      // 2. Fetch users for this organization across roles from backend APIs
      if (axios) {
        try {
          const rolesToFetch = ['FACULTY', 'ADMIN', 'STAFF', 'STUDENT', 'PARENT'];
          const requests = rolesToFetch.map(type =>
            axios.get(`${configData.GET_USERS_BY_TYPE}/${targetOrg}`, {
              params: { userType: type, pageSize: 500 },
              headers: { accept: 'application/hal+json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
              timeout: 15000
            }).catch(err => ({ error: err, userType: type }))
          );

          const responses = await Promise.all(requests);

          responses.forEach((res) => {
            if (!res || res.error) return;
            const items = res.data?.items || res.data?.content || (Array.isArray(res.data) ? res.data : []);

            items.forEach((item) => {
              const u = item.user || item;
              const email = (u.email || item.email || '').toLowerCase().trim();
              const id = u.userId || item.userId || u.id || item.id;
              const userOrgId = (u.orgId || item.orgId || '').toLowerCase().trim();

              // Strict tenant isolation: do not include users belonging to other organizations or parent org
              if (userOrgId && userOrgId !== targetOrg.toLowerCase()) {
                return;
              }

              // Explicitly exclude any user whose org is the parent org (when targetOrg is this specific branch)
              if (knownParentOrgId && userOrgId === knownParentOrgId.toLowerCase() && targetOrg.toLowerCase() !== knownParentOrgId.toLowerCase()) {
                return;
              }

              if (email && seenEmails.has(email)) return;
              if (id && seenIds.has(id)) return;

              if (email) seenEmails.add(email);
              if (id) seenIds.add(id);

              const firstName = u.firstName || item.firstName || '';
              const lastName = u.lastName || item.lastName || '';
              const fullName = `${firstName} ${lastName}`.trim() || u.name || item.name || u.userName || (email ? email.split('@')[0] : 'User');

              // Normalize role
              const rawRole = u.userType || item.userType || u.role || item.role || 'STUDENT';
              let role = String(rawRole).toUpperCase();
              if (role === 'FACULTY') role = 'TEACHER';

              const lastActive = u.lastLoginDate || item.lastLoginDate || u.lastUpdatedAt || item.lastUpdatedAt || u.lastUpdatedTime || u.updatedDate || item.updatedDate || u.createdDate || item.createdDate;
              const parsedDev = parseDeviceInfo(u.userAgent || item.userAgent || '');

              rawUserPool.push({
                id: id || `usr-${rawUserPool.length + 1}`,
                name: fullName,
                email: email || 'N/A',
                role: role,
                userStatus: (u.userStatus || item.userStatus || 'ACTIVE').toUpperCase(),
                lastActive: lastActive,
                parsedDev: parsedDev,
                loginCount: Number(u.loginCount || item.loginCount || u.sessionCount || 1),
                branch: currentOrgName || u.orgName || item.orgName || currentUser?.orgName || 'Main Campus',
                orgId: targetOrg,
                isCurrentUser: false
              });
            });
          });
        } catch (backendErr) {
          console.warn('[FirebaseAnalytics] Backend users fetch error:', backendErr);
        }
      }

      // 3. Attach stored live sessions strictly to registered users belonging to this organization
      Object.values(liveSessions).forEach(session => {
        const sessionOrgId = session.orgId ? String(session.orgId).trim().toLowerCase() : '';
        // Strict tenant isolation: do not include sessions from other organizations
        if (!sessionOrgId || sessionOrgId !== targetOrg.toLowerCase()) {
          return;
        }

        const sessionEmail = (session.email || '').toLowerCase().trim();
        const sessionId = session.id;

        // ONLY attach live session state to an existing user in this organization's official roster
        const existing = rawUserPool.find(u =>
          (sessionEmail && u.email && u.email.toLowerCase() === sessionEmail) ||
          (sessionId && u.id && u.id === sessionId)
        );
        if (existing) {
          existing.liveSession = session;
        }
        // Unverified sessions or new signups from other orgs are NEVER injected into the official roster
      });

      // 4. Status resolution
      const now = Date.now();
      const activeWindowMs = 30 * 60 * 1000; // 30 minutes
      const idleWindowMs = 2 * 60 * 60 * 1000; // 2 hours

      const loadedUsers = rawUserPool.map((u) => {
        if (u.isCurrentUser) {
          return {
            ...u,
            status: 'ACTIVE_NOW',
            currentScreen: 'Firebase Analytics Dashboard',
            device: u.parsedDev?.label || 'Web (Desktop Browser)',
            deviceType: u.parsedDev?.type || 'desktop',
            monthlySessions: Math.max(u.loginCount || 1, 6),
            lastSeen: 'Active right now',
            lastSeenDate: new Date()
          };
        }

        const session = u.liveSession || liveSessions[u.email?.toLowerCase()] || liveSessions[u.id];
        let status = 'OFFLINE';
        let currentScreen = u.role === 'TEACHER' ? 'Classes & Attendance' : (u.role === 'STUDENT' ? 'Student Portal' : 'Dashboard / Workspace');
        let deviceLabel = u.parsedDev?.label || 'Web (Chrome • macOS)';
        let deviceType = u.parsedDev?.type || 'desktop';
        let displayLastSeen = formatRelativeTime(u.lastActive);
        let lastSeenDate = u.lastActive ? new Date(u.lastActive) : null;

        if (session && session.lastActive) {
          const diffMs = now - session.lastActive;
          if (diffMs <= activeWindowMs) {
            status = 'ACTIVE_NOW';
            displayLastSeen = 'Active right now';
            lastSeenDate = new Date(session.lastActive);
            currentScreen = session.currentScreen || currentScreen;
            deviceLabel = session.device || deviceLabel;
            deviceType = session.deviceType || deviceType;
          } else if (diffMs <= idleWindowMs) {
            status = 'IDLE';
            displayLastSeen = formatRelativeTime(session.lastActive);
            lastSeenDate = new Date(session.lastActive);
          }
        } else if (u.lastActive) {
          const userDate = new Date(u.lastActive);
          if (!isNaN(userDate.getTime())) {
            const diffMs = now - userDate.getTime();
            if (diffMs <= activeWindowMs) {
              status = 'ACTIVE_NOW';
              displayLastSeen = 'Active right now';
            } else if (diffMs <= idleWindowMs || userDate.toDateString() === new Date().toDateString()) {
              status = 'IDLE';
              displayLastSeen = formatRelativeTime(u.lastActive);
            }
          }
        }

        return {
          id: u.id,
          name: u.name,
          email: u.email,
          role: u.role,
          status: status,
          currentScreen: currentScreen,
          device: deviceLabel,
          deviceType: deviceType,
          monthlySessions: Math.max(u.loginCount || 1, 1),
          lastSeen: displayLastSeen,
          lastSeenDate: lastSeenDate,
          branch: u.branch,
          isCurrentUser: false,
          userStatus: u.userStatus || 'ACTIVE',
          orgId: u.orgId
        };
      });

      // Sort: Current user (You) first, then Active Now, then Idle, then Offline
      loadedUsers.sort((a, b) => {
        if (a.isCurrentUser) return -1;
        if (b.isCurrentUser) return 1;
        if (a.status === 'ACTIVE_NOW' && b.status !== 'ACTIVE_NOW') return -1;
        if (b.status === 'ACTIVE_NOW' && a.status !== 'ACTIVE_NOW') return 1;
        if (a.status === 'IDLE' && b.status === 'OFFLINE') return -1;
        if (b.status === 'IDLE' && a.status === 'OFFLINE') return 1;
        return (a.name || '').localeCompare(b.name || '');
      });

      setUserList(loadedUsers);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      console.warn('[FirebaseAnalytics] fetchAllUsers error:', err);
      setFetchError('Error synchronizing active user list. Displaying live session state.');
    } finally {
      setIsRefreshing(false);
    }
  }, [currentUser, userRole, effectiveOrgId, authParentOrgId, axios, token, currentOrgName]);

  // Initial load, periodic auto-refresh, and immediate cross-tab presence subscription
  useEffect(() => {
    fetchAllUsers();

    // Auto-refresh presence every 15 seconds
    const interval = setInterval(() => {
      fetchAllUsers();
    }, 15000);

    // Subscribe to immediate cross-tab/window presence updates
    const unsubscribe = subscribeToPresenceUpdates(() => {
      fetchAllUsers();
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [fetchAllUsers]);

  // Dynamic KPI Metrics (100% computed from real loaded data)
  const totalUsersCount = userList.length;
  const activeNowCount = userList.filter(u => u.status === 'ACTIVE_NOW').length;
  const idleCount = userList.filter(u => u.status === 'IDLE').length;

  // Real Daily Active Users (users active today or currently online/idle)
  const dauCount = useMemo(() => {
    return userList.filter(u => {
      if (u.status === 'ACTIVE_NOW' || u.status === 'IDLE') return true;
      if (!u.lastSeenDate) return false;
      const today = new Date();
      const userDate = new Date(u.lastSeenDate);
      return (
        userDate.getDate() === today.getDate() &&
        userDate.getMonth() === today.getMonth() &&
        userDate.getFullYear() === today.getFullYear()
      );
    }).length;
  }, [userList]);


  // Dynamic Role Breakdown
  const roleBreakdown = useMemo(() => {
    const counts = {
      PARENT: 0,
      TEACHER: 0,
      STUDENT: 0,
      ADMIN: 0,
      STAFF: 0
    };

    userList.forEach(u => {
      const r = u.role;
      if (counts[r] !== undefined) {
        counts[r]++;
      } else if (r === 'FACULTY') {
        counts.TEACHER++;
      } else {
        counts.STAFF++;
      }
    });

    const total = userList.length || 1;
    return {
      parent: { count: counts.PARENT, pct: Math.round((counts.PARENT / total) * 100) },
      teacher: { count: counts.TEACHER, pct: Math.round((counts.TEACHER / total) * 100) },
      student: { count: counts.STUDENT, pct: Math.round((counts.STUDENT / total) * 100) },
      admin: { count: counts.ADMIN, pct: Math.round((counts.ADMIN / total) * 100) },
      staff: { count: counts.STAFF, pct: Math.round((counts.STAFF / total) * 100) }
    };
  }, [userList]);

  // Dynamic Platform Breakdown
  const platformStats = useMemo(() => {
    if (userList.length === 0) return { webPct: 100, mobilePct: 0 };
    const mobileCount = userList.filter(u => u.deviceType === 'mobile').length;
    const webCount = userList.length - mobileCount;
    const webPct = Math.round((webCount / userList.length) * 100);
    return {
      webPct,
      mobilePct: 100 - webPct
    };
  }, [userList]);

  // Dynamic Average Sessions / Engagement
  const avgMonthlySessions = useMemo(() => {
    if (userList.length === 0) return 0;
    const totalSessions = userList.reduce((sum, u) => sum + (u.monthlySessions || 1), 0);
    return (totalSessions / userList.length).toFixed(1);
  }, [userList]);

  // Filtered Users List for the Table
  const filteredUsers = useMemo(() => {
    return userList.filter(user => {
      const query = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !query ||
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query) ||
        user.branch?.toLowerCase().includes(query);

      const matchesRole =
        roleFilter === 'ALL' ||
        user.role === roleFilter ||
        (roleFilter === 'TEACHER' && user.role === 'FACULTY');

      let matchesStatus = false;
      if (statusFilter === 'ACTIVE_AND_IDLE') {
        matchesStatus = user.status === 'ACTIVE_NOW' || user.status === 'IDLE';
      } else {
        // Default: strictly Active Now
        matchesStatus = user.status === 'ACTIVE_NOW';
      }

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [userList, searchTerm, roleFilter, statusFilter]);

  // Export CSV
  const exportCsv = () => {
    if (filteredUsers.length === 0) return;
    const headers = ["Name,Email,Status,Current Screen,Sessions,Last Seen"];
    const rows = filteredUsers.map(u =>
      `"${u.name}","${u.email}","${u.status}","${u.currentScreen}","${u.monthlySessions || 1}","${u.lastSeen}"`
    );
    const blob = new Blob([[...headers, ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Firebase_Active_Users_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`firebase-analytics-page ${embedded ? 'is-embedded' : ''}`}>
      {/* Top Banner / Header */}
      <header className="fa-header">
        <div className="fa-header-main">
          <div className="fa-title-wrap">
            <div className="fa-title-icon-badge">
              <AnalyticsIcon className="fa-title-icon" />
            </div>
            <div>
              <div className="fa-title-row">
                <h1 className="fa-title">Firebase Analytics & User Activity</h1>
                <span className="fa-live-badge">
                  <span className="fa-pulse-dot"></span> Live Stream Active
                </span>
              </div>
              <p className="fa-subtitle">
                Live user sessions, Daily Active Users (DAU), and average sessions tracked via Google Analytics.
              </p>
            </div>
          </div>

          <div className="fa-header-actions">
            <div className="fa-org-badge">
              <CorporateFareIcon className="fa-org-icon" fontSize="small" />
              <span>Organization: <strong>{currentOrgName || 'Active Organization'}</strong></span>
            </div>

            <button
              className="fa-btn-refresh"
              onClick={fetchAllUsers}
              disabled={isRefreshing}
              title="Refresh Activity"
            >
              <RefreshIcon className={`fa-refresh-icon ${isRefreshing ? 'is-spinning' : ''}`} />
              <span>{isRefreshing ? 'Syncing...' : 'Refresh'}</span>
            </button>

            <button
              className="fa-btn-export"
              onClick={exportCsv}
              disabled={filteredUsers.length === 0}
              title="Export CSV list"
            >
              <DownloadIcon fontSize="small" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        <div className="fa-sync-status">
          <span>Last synchronized: <strong>{lastUpdated || 'Connecting...'}</strong></span>
          <span className="fa-divider">•</span>
          <span>Total Tracked Users: <strong>{totalUsersCount}</strong></span>
          <span className="fa-divider">•</span>
          <span className="fa-isolation-tag">
            <SecurityIcon className="fa-security-icon" fontSize="inherit" /> Scope: {currentOrgName || 'Active Organization'} only
          </span>
        </div>

        {fetchError && (
          <div className="fa-error-notice">
            <span>ℹ️ {fetchError}</span>
          </div>
        )}
      </header>

      {/* Dynamic KPI Stats Cards */}
      <section className="fa-kpi-grid">
        {/* Card 1: Active Users Right Now */}
        <div className="fa-kpi-card fa-card-active-now">
          <div className="fa-kpi-header">
            <span className="fa-kpi-label">Active Users Now</span>
            <div className="fa-kpi-icon-wrap fa-icon-active">
              <CircleIcon className="fa-dot-icon" />
            </div>
          </div>
          <div className="fa-kpi-value-row">
            <span className="fa-kpi-value">{activeNowCount}</span>
            <span className="fa-kpi-subtext">live on web & app</span>
          </div>
          <div className="fa-kpi-footer">
            <span className="fa-badge-green">● Realtime</span>
            <span className="fa-footer-info">+{idleCount} idle in last 30m</span>
          </div>
        </div>

        {/* Card 2: Daily Active Users (DAU) */}
        <div className="fa-kpi-card">
          <div className="fa-kpi-header">
            <span className="fa-kpi-label">Daily Active Users (DAU)</span>
            <div className="fa-kpi-icon-wrap fa-icon-dau">
              <PeopleIcon fontSize="small" />
            </div>
          </div>
          <div className="fa-kpi-value-row">
            <span className="fa-kpi-value">{dauCount}</span>
            <span className="fa-kpi-subtext">active today</span>
          </div>
          <div className="fa-kpi-footer">
            <span className="fa-footer-info">Users with sessions today</span>
          </div>
        </div>


        {/* Card 4: Avg Sessions per User */}
        <div className="fa-kpi-card">
          <div className="fa-kpi-header">
            <span className="fa-kpi-label">Avg. Sessions / User</span>
            <div className="fa-kpi-icon-wrap fa-icon-time">
              <AccessTimeIcon fontSize="small" />
            </div>
          </div>
          <div className="fa-kpi-value-row">
            <span className="fa-kpi-value">{avgMonthlySessions}</span>
            <span className="fa-kpi-subtext">sessions/mo</span>
          </div>
          <div className="fa-kpi-footer">
            <span className="fa-footer-info">Average frequency per active user</span>
          </div>
        </div>

        {/* Card 5: Mobile vs Web Share */}
        <div className="fa-kpi-card">
          <div className="fa-kpi-header">
            <span className="fa-kpi-label">Platform Share</span>
            <div className="fa-kpi-icon-wrap fa-icon-device">
              <DevicesIcon fontSize="small" />
            </div>
          </div>
          <div className="fa-kpi-value-row">
            <span className="fa-kpi-value">{platformStats.webPct}% Web</span>
            <span className="fa-footer-info">/ {platformStats.mobilePct}% App</span>
          </div>
          <div className="fa-platform-bar">
            <div className="fa-bar-web" style={{ width: `${platformStats.webPct}%` }} title={`${platformStats.webPct}% Desktop Web`}></div>
            <div className="fa-bar-mobile" style={{ width: `${platformStats.mobilePct}%` }} title={`${platformStats.mobilePct}% Mobile App`}></div>
          </div>
        </div>
      </section>

      {/* Simple List & Table of Active & Monthly Users */}
      <section className="fa-table-section">
        {/* Table Filters Bar */}
        <div className="fa-filter-bar">
          <div className="fa-search-box">
            <SearchIcon className="fa-search-icon" />
            <input
              type="text"
              placeholder="Search active users by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="fa-search-input"
            />
            {searchTerm && (
              <button className="fa-clear-search" onClick={() => setSearchTerm('')}>×</button>
            )}
          </div>

          <div className="fa-filter-controls">
            {/* Status Filter */}
            <div className="fa-filter-group">
              <FilterListIcon fontSize="small" className="fa-filter-label-icon" />
              <select
                className="fa-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ACTIVE_NOW">🟢 Active Users Only ({activeNowCount})</option>
                <option value="ACTIVE_AND_IDLE">🟡 Active & Idle Users ({activeNowCount + idleCount})</option>
              </select>
            </div>

            {/* Role Filter */}
            <div className="fa-filter-group">
              <select
                className="fa-select"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="ALL">All Roles</option>
                <option value="ADMIN">Administrators ({roleBreakdown.admin.count})</option>
                <option value="TEACHER">Teachers ({roleBreakdown.teacher.count})</option>
                <option value="PARENT">Parents ({roleBreakdown.parent.count})</option>
                <option value="STUDENT">Students ({roleBreakdown.student.count})</option>
              </select>
            </div>
          </div>
        </div>

        {/* User Count Meta */}
        <div className="fa-table-meta-row">
          <span>
            Showing <strong>{filteredUsers.length}</strong> {statusFilter === 'ACTIVE_NOW' ? 'active user' : 'active/idle user'}{filteredUsers.length === 1 ? '' : 's'} in {currentOrgName || 'this organization'}
          </span>
          {statusFilter === 'ACTIVE_NOW' && (
            <span className="fa-filter-active-indicator">
              ● Live active users only
            </span>
          )}
        </div>

        {/* Clean Users Table */}
        <div className="fa-table-wrapper">
          <table className="fa-table">
            <thead>
              <tr>
                <th>User Details</th>
                <th>Activity Status</th>
                <th>Current Screen / Action</th>
                <th>Sessions</th>
                <th>Last Active</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="fa-empty-state">
                    <PersonOutlineIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
                    <p>No active users live right now in {currentOrgName || 'this organization'}.</p>
                    {(searchTerm || roleFilter !== 'ALL' || statusFilter !== 'ACTIVE_NOW') ? (
                      <button
                        className="fa-reset-btn"
                        onClick={() => {
                          setSearchTerm('');
                          setRoleFilter('ALL');
                          setStatusFilter('ACTIVE_NOW');
                        }}
                      >
                        Reset to Active Users
                      </button>
                    ) : (
                      <button className="fa-reset-btn" onClick={fetchAllUsers}>
                        Refresh Users
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const initials = user.name
                    ? user.name
                      .replace('(You)', '')
                      .trim()
                      .split(' ')
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()
                    : 'U';

                  const roleClass = (user.role || 'STUDENT').toLowerCase();

                  return (
                    <tr
                      key={user.id}
                      className={user.isCurrentUser ? 'fa-row-current-user' : ''}
                    >
                      {/* Name & Email */}
                      <td>
                        <div className="fa-user-cell">
                          <div className={`fa-avatar role-avatar-${roleClass}`}>
                            {initials}
                          </div>
                          <div className="fa-user-info">
                            <span className="fa-user-name">
                              {user.name}
                              {user.isCurrentUser && (
                                <span className="fa-you-tag">You</span>
                              )}
                            </span>
                            <span className="fa-user-email">{user.email}</span>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td>
                        {user.status === 'ACTIVE_NOW' && (
                          <span className="fa-status-pill status-active">
                            <span className="fa-pulse-dot-small"></span> Active Now
                          </span>
                        )}
                        {user.status === 'IDLE' && (
                          <span className="fa-status-pill status-idle">
                            <span className="fa-idle-dot"></span> Idle
                          </span>
                        )}
                        {user.status === 'OFFLINE' && (
                          <span className="fa-status-pill status-offline">
                            <span className="fa-offline-dot"></span> Offline
                          </span>
                        )}
                      </td>

                      {/* Current Screen */}
                      <td>
                        <span className="fa-screen-label">{user.currentScreen}</span>
                        <div className="fa-branch-label">{user.branch}</div>
                      </td>

                      {/* Sessions */}
                      <td>
                        <div className="fa-session-cell">
                          <span className="fa-session-count">{user.monthlySessions || 1}</span>
                          <span className="fa-session-sub">{user.monthlySessions === 1 ? 'session' : 'sessions'}</span>
                        </div>
                      </td>

                      {/* Last Seen */}
                      <td>
                        <span className="fa-lastseen-text">{user.lastSeen}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default FirebaseAnalytics;
